"use client";

import * as React from "react";
import { PageHeaderV2, type PageHeaderV2Config } from "components/ui/v2/page-header-v2";
import { PageLoadingStateV2 } from "components/ui/v2/maintainer/page-loading-state-v2";
import { PageBanner } from "./page-banner";
import { PageBody } from "./page-body";
import { PageContainer } from "./page-container";
import { PageFab } from "./page-fab";
import { PageOverlays } from "./page-overlays";
import { PageRoot } from "./page-root";

export interface PageShellProps {
  header?: React.ReactNode | PageHeaderV2Config;
  toolbar?: React.ReactNode;
  beforeContent?: React.ReactNode;
  /** Optional featured rail — after toolbar, before body (§6.11 FeaturedListV2). */
  featuredList?: React.ReactNode;
  loading?: boolean;
  loadingTitle?: string;
  loadingDescription?: string;
  loadingState?: React.ReactNode;
  isEmpty?: boolean;
  emptyState?: React.ReactNode;
  metaBar?: React.ReactNode;
  children?: React.ReactNode;
  paginationFooter?: React.ReactNode;
  helpTooltips?: React.ReactNode;
  overlays?: React.ReactNode;
  fab?: React.ReactNode;
}

export function PageShell({
  header,
  toolbar,
  beforeContent,
  featuredList,
  loading = false,
  loadingTitle,
  loadingDescription,
  loadingState,
  isEmpty = false,
  emptyState,
  metaBar,
  children,
  paginationFooter,
  helpTooltips,
  overlays,
  fab,
}: PageShellProps) {
  // Initial load only: keep grid on refetch; featured rail can show while catalog loads.
  const showInitialLoading = Boolean(loading && isEmpty && !featuredList);

  const headerNode =
    header == null
      ? null
      : React.isValidElement(header)
        ? header
        : <PageHeaderV2 {...(header as PageHeaderV2Config)} />;

  return (
    <PageRoot>
      <PageContainer>
        {beforeContent ? <PageBanner>{beforeContent}</PageBanner> : null}
        {headerNode}
        {toolbar}
        {featuredList}

        {showInitialLoading ? (
          loadingState ?? (
            <PageLoadingStateV2
              title={loadingTitle ?? "Cargando..."}
              description={loadingDescription}
            />
          )
        ) : isEmpty ? (
          emptyState
        ) : (
          <>
            {metaBar}
            <PageBody>{children}</PageBody>
            {paginationFooter}
          </>
        )}

        {helpTooltips}
      </PageContainer>

      <PageOverlays>{overlays}</PageOverlays>
      <PageFab>{fab}</PageFab>
    </PageRoot>
  );
}
